/**
 * TemplateChip — dual-mode component:
 * - Single-project mode: read-only pill showing "evaluated with [Template]"
 *   Clicking enters aggregate mode.
 * - Aggregate mode: clickable dropdown to switch between templates.
 */

import { ChevronDown, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';

// ============================================================================
// COMPONENT
// ============================================================================

export function TemplateChip() {
  const {
    filterMode,
    activeTemplate,
    templates,
    templatesLoading,
    enterAggregateMode,
    switchTemplate,
  } = useDashboardContext();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  // Loading skeleton
  if (templatesLoading || !activeTemplate) {
    return <div className="animate-pulse h-6 w-40 rounded-full bg-muted" />;
  }

  // ---- Single-project mode: read-only pill ----
  if (filterMode === 'single-project') {
    return (
      <button
        onClick={enterAggregateMode}
        className={cn(
          'bg-brand/10 text-brand text-xs rounded-full px-3 py-1',
          'cursor-pointer hover:bg-brand/20 transition-colors'
        )}
        title="Click to view all projects with this template"
      >
        evaluated with{' '}
        <span className="font-medium">{activeTemplate.name}</span>
      </button>
    );
  }

  // ---- Aggregate mode: template picker dropdown ----
  function handleSelect(templateId: string) {
    switchTemplate(templateId);
    setOpen(false);
  }

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger pill */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1 bg-brand/10 text-brand text-xs rounded-full px-3 py-1',
          'border border-brand/30 cursor-pointer hover:bg-brand/20 transition-colors'
        )}
      >
        in <span className="font-medium ml-0.5">{activeTemplate.name}</span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 w-64 rounded-xl border border-border-subtle bg-surface shadow-lg py-1">
          {templates.map((t) => (
            <button
              key={t.templateId}
              onClick={() => handleSelect(t.templateId)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-subtle transition-colors',
                t.templateId === activeTemplate.templateId && 'bg-brand/5'
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-primary truncate">{t.name}</div>
                {t.description && (
                  <div className="text-xs text-secondary truncate">{t.description}</div>
                )}
              </div>
              {t.templateId === activeTemplate.templateId && (
                <Check className="w-4 h-4 text-brand flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
